import { proxy } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import type { MulticardConfig, PaymentType } from '@/lib/payment/types';
import { StorageHelper } from '@/lib/storage';

const storageHelper = new StorageHelper();
const localRepairItems = storageHelper.getSessionStorage(
  'SERVICE_REPAIR_ITEMS',
);

export type PaymentMethodConfiguration = {
  dropInConfiguration: Record<
    string,
    string | string[] | number | object | boolean
  > | null;
  name: string;
  type: PaymentType;
};

export type PaymentGatewayConfig = {
  id: string;
  paymentMethods: PaymentMethodConfiguration[];
};

export interface PaymentGatewaysState {
  amount: number | null;
  configs: PaymentGatewayConfig[];
  id: string | null;
  isMulticard?: boolean;
  multicardConfig?: MulticardConfig;
  partialAmount?: number | null;
  repairItems: string[];
}

export const paymentGatewaysState = proxy<PaymentGatewaysState>({
  configs: [],
  multicardConfig: undefined,
  id: null,
  isMulticard: false,
  amount: null,
  partialAmount: null,
  repairItems: localRepairItems ?? [],
});

export const paymentGatewaysActions = {
  clear: (keepPartialAmount = false) => {
    paymentGatewaysState.id = null;
    paymentGatewaysState.amount = null;
    paymentGatewaysState.configs = [];
    paymentGatewaysState.repairItems = [];
    paymentGatewaysState.isMulticard = false;
    if (!keepPartialAmount) paymentGatewaysState.partialAmount = null;
    paymentGatewaysState.multicardConfig = undefined;
  },

  set: (id: string, amount: number, configs: PaymentGatewayConfig[]) => {
    paymentGatewaysState.id = id;
    paymentGatewaysState.amount = amount;
    paymentGatewaysState.configs = configs;
  },
  setRepairItems: (repairItems: string[]) => {
    paymentGatewaysState.repairItems = repairItems;
  },
  setMulticardConfig: (multicardConfig: MulticardConfig) => {
    paymentGatewaysState.multicardConfig = multicardConfig;
    const hasMulticardPayment =
      !!multicardConfig?.maxPayments && multicardConfig.maxPayments < 4;
    if (hasMulticardPayment) {
      paymentGatewaysState.isMulticard = true;
      paymentGatewaysState.partialAmount = multicardConfig?.maxAmount;
    }
  },
  setIsMulticard: (checked: boolean) => {
    paymentGatewaysState.isMulticard = checked;
  },
  setPartialAmount: (amount: number) => {
    paymentGatewaysState.partialAmount = amount;
  },
};

subscribeKey(
  paymentGatewaysState,
  'repairItems',
  async (repairItems: PaymentGatewaysState['repairItems']) => {
    storageHelper.setSessionStorage('SERVICE_REPAIR_ITEMS', repairItems);
  },
);
